import { useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { useSelector } from "react-redux"
import { Button } from "@/components/ui/button"
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"
import {
  DriverDisplayInfo,
  selectDriverRouteAndLocationDetailsMap,
} from "@/reducers/accountChatsSelectors"
import { Location } from "@/reducers/locationsSlice"
import { Check, ChevronDown, MapPin } from "lucide-react"
import { cn } from "@/lib/utils"
import { Participant } from "@/reducers/messagesSlice"
import { participantPhotoToSrc } from "@/components/AvatarImage"

export type SelectedDriverLocation = {
  driverJid: string
  driverName: string
  driverInitials: string
  location: Location | undefined
  isFromRoute: boolean
}

const getInitials = (name: string) => {
  return name
    .split(" ")
    .map((word) => word[0])
    .join("")
    .toUpperCase()
    .slice(0, 2)
}

export const DriverAvatar = ({
  driver,
  size = "default",
}: {
  driver: Participant
  size?: "default" | "xs"
}) => {
  const avatarPhoto = useMemo(
    () => participantPhotoToSrc(driver?.photo),
    [driver?.photo],
  )

  const avatarName = useMemo(
    () => (driver ? `${driver.firstName} ${driver.lastName}` : ""),
    [driver],
  )

  const isExtraSmall = size === "xs"

  return (
    <div className="relative self-center">
      <Avatar className={cn("h-8 w-8", isExtraSmall && "h-6 w-6")}>
        {avatarPhoto && <AvatarImage src={avatarPhoto} />}
        <AvatarFallback
          style={{ backgroundColor: "var(--defaultAvatarColor)" }}
          className={cn(
            "font-medium text-gray-900 dark:text-gray-100",
            isExtraSmall && "text-xs",
          )}
        >
          {getInitials(avatarName ?? "")}
        </AvatarFallback>
      </Avatar>
    </div>
  )
}

interface DriverSelectProps {
  value?: string
  onValueChange: (driverLocation: SelectedDriverLocation | undefined) => void
  placeholder?: string
}

const DriverDisplayInfoAvatar = ({
  driver,
  size = "default",
}: {
  driver: DriverDisplayInfo
  size?: "default" | "xs"
}) => {
  const isExtraSmall = size === "xs"
  return (
    <div className="relative self-center">
      <Avatar className={cn("h-8 w-8", isExtraSmall && "h-6 w-6")}>
        {driver.avatarPhoto && <AvatarImage src={driver.avatarPhoto} />}
        <AvatarFallback
          style={{ backgroundColor: "var(--defaultAvatarColor)" }}
          className={cn(
            "font-medium text-gray-900 dark:text-gray-100",
            isExtraSmall && "text-xs",
          )}
        >
          {getInitials(driver.avatarName)}
        </AvatarFallback>
      </Avatar>
    </div>
  )
}

const getDriverSurname = (driver: DriverDisplayInfo) => {
  const source = driver.avatarName || driver.name
  const parts = source.trim().split(" ")
  return parts[parts.length - 1].toLowerCase()
}

// Returns the last stop on the route (preferred for "destination") or the
// current GPS location as fallback.
const getDriverLocationInfo = (driver: DriverDisplayInfo) => {
  if (driver.stops && driver.stops.length > 0) {
    const lastStop = driver.stops[driver.stops.length - 1]
    const address =
      lastStop.location.formattedAddress ||
      `${lastStop.location.street || ""} ${lastStop.location.locality || ""}`.trim() ||
      (lastStop.location.lat && lastStop.location.lon
        ? `${lastStop.location.lat}, ${lastStop.location.lon}`
        : null)
    return {
      text: address,
      isFromRoute: true,
      location: lastStop.location,
      hasLocation: !!address,
    }
  }

  const address =
    driver.location.formattedAddress ||
    `${driver.location.street || ""} ${driver.location.locality || ""}`.trim() ||
    (driver.location.lat && driver.location.lon
      ? `${driver.location.lat}, ${driver.location.lon}`
      : null)
  return {
    text: address,
    isFromRoute: false,
    location: driver.location,
    hasLocation: !!address,
  }
}

export default function DriverSelect({
  value,
  onValueChange,
  placeholder,
}: DriverSelectProps) {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const driversMap = useSelector(selectDriverRouteAndLocationDetailsMap)

  const driversWithLocations = useMemo(() => {
    return Object.values(driversMap)
      .filter((driver) => getDriverLocationInfo(driver).hasLocation)
      .sort((a, b) => getDriverSurname(a).localeCompare(getDriverSurname(b)))
  }, [driversMap])

  const handleSelect = (driverJid: string) => {
    if (driverJid === value) {
      onValueChange(undefined)
      setOpen(false)
      return
    }

    const selectedDriver = driversWithLocations.find((d) => d.jid === driverJid)

    if (!selectedDriver) {
      onValueChange(undefined)
      setOpen(false)
      return
    }

    const locationInfo = getDriverLocationInfo(selectedDriver)

    onValueChange({
      driverJid: selectedDriver.jid,
      driverName: selectedDriver.name,
      driverInitials: getInitials(selectedDriver.avatarName),
      location: locationInfo.location,
      isFromRoute: locationInfo.isFromRoute,
    })
    setOpen(false)
  }

  const selectedDriverData = value
    ? driversWithLocations.find((d) => d.jid === value)
    : undefined

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="w-full min-w-0 justify-between rounded-md border px-4 py-2 pr-2.5 text-left font-medium hover:bg-blue-100 dark:hover:bg-blue-800"
        >
          <div className="flex items-center gap-2 truncate">
            {selectedDriverData && (
              <DriverDisplayInfoAvatar driver={selectedDriverData} size="xs" />
            )}
            <span className="truncate">
              {selectedDriverData?.name ||
                (placeholder ?? t("planOrder.driverSelect.placeholder"))}
            </span>
          </div>
          <ChevronDown className="ml-2 h-4 w-4 shrink-0 text-gray-500" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[450px] p-0" align="start">
        <Command>
          <CommandInput placeholder={t("planOrder.driverSelect.search")} />
          <CommandList>
            <CommandEmpty>{t("planOrder.driverSelect.empty")}</CommandEmpty>
            <CommandGroup>
              {driversWithLocations.map((driver) => {
                const locationInfo = getDriverLocationInfo(driver)

                return (
                  <CommandItem
                    key={driver.jid}
                    value={`${driver.name} ${locationInfo.text}`}
                    onSelect={() => handleSelect(driver.jid)}
                  >
                    <Check
                      className={cn(
                        "mr-2 h-4 w-4",
                        value === driver.jid ? "opacity-100" : "opacity-0",
                      )}
                    />
                    <DriverDisplayInfoAvatar driver={driver} />
                    <div className="flex flex-1 flex-col gap-1">
                      <div className="flex items-center gap-2">
                        <span className="font-medium">{driver.name}</span>
                      </div>
                      <div className="flex items-center gap-1 text-xs text-muted-foreground">
                        <MapPin className="h-3 w-3" />
                        <span
                          className="truncate"
                          title={locationInfo.text || undefined}
                        >
                          {locationInfo.text}
                        </span>
                      </div>
                    </div>
                  </CommandItem>
                )
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}
